import {
	CheckSquareOutlined,
	DeleteOutlined,
	EditOutlined,
	FileDoneOutlined,
	RollbackOutlined,
} from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { Segmented, type SegmentedProps } from "components/Segmented/Segmented"
import { Tag, type TagProps } from "components/Tag/Tag"

import { AddressLink } from "components/AddressLink/AddressLink"
import { DatePicker } from "components/DatePicker/DatePicker"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { Modal } from "components/Modal/Modal"
import { useNotification } from "components/Notification/Notification"
import { SearchInput } from "components/SearchInput/SearchInput"
import { RangeFilter } from "components/Table/RangeFilter"
import {
	Table,
	type ColumnType,
	type ExpandableConfig,
} from "components/Table/Table"
import { useTableParams } from "components/Table/useTableParams"
import { Tooltip } from "components/Tooltip/Tooltip"
import { useDisclosure } from "hooks/useDisclosure"
import { Enrollment, STATUSES } from "interface/enrollment"
import { memoize } from "lodash-es"
import { ConsentActions } from "modules/enrollments/ConsentActions"
import { EnrollForm } from "modules/enrollments/EnrollForm"
import { MedicalNecessityFormActions } from "modules/enrollments/MedicalNecessityFormActions"
import { ToggleEnrollment } from "modules/enrollments/ToggleEnrollment"
import { AlternateContactsList } from "modules/patient/AlternateContactsList"
import {
	ComponentProps,
	ReactNode,
	useCallback,
	useMemo,
	useState,
} from "react"
import {
	useAdminEnrollmentReady,
	useAdminEnrollments,
	useAdminEnrollmentToggle,
	useAdminEnrollmentUpdate,
} from "services/enrollments"
import { DEFAULT_PLACEHOLDER } from "utils/constants"
import { asDate, Dayjs, formatDate, today } from "utils/date"
import { prettyprintPhoneNumber } from "utils/patient"
import { SelectOrganization } from "./SelectOrganization"
import { SelectTeams } from "./SelectTeams"

export const AdminEnrollments = () => {
	const {
		set,
		page,
		sort,
		query,
		status,
		organizations,
		createdAtStart,
		createdAtEnd,
	} = useTableParams({
		status: ALL_STATUSES,
		sort: ["createdAt-descending"],
		organizations: [],
		createdAtStart: "",
		createdAtEnd: "",
	})
	const { data: { result: enrollments = [], total } = {}, isFetching } =
		useAdminEnrollments({
			status,
			sort,
			page,
			query,
			organizations,
			createdAtStart,
			createdAtEnd,
		})
	const handleTableChange = useCallback<
		NonNullable<TableEnrollmentsType["onChange"]>
	>(
		(pagination, _, sorter) => {
			const { field, order } = Array.isArray(sorter) ? sorter[0] : sorter
			set(
				["page", String(pagination.current)],
				["sort", field && order ? [`${field}-${order}ing`] : []],
			)
		},
		[set],
	)

	return (
		<div className="flex h-full min-h-0 flex-col gap-3">
			<h2 className="font-nunito text-3xl font-semibold">Enrollments</h2>

			<div className="flex h-full min-h-0 flex-col gap-2">
				<Status value={status} onChange={(value) => set(["status", value])} />

				<div className="flex flex-wrap gap-2">
					<SearchInput
						size="large"
						value={query}
						className="max-w-xl"
						placeholder="Search enrollments"
						onSearch={(value) => set(["query", value])}
					/>
					<SelectOrganization
						size="large"
						aria-label="Organizations"
						value={organizations}
						className="min-w-xs"
						mode="multiple"
						placeholder="Select one or more organizations"
						onChange={(value) => set(["organizations", value])}
					/>
					<RangeFilter
						size="large"
						allowEmpty
						label="Created at"
						value={[
							createdAtStart.length > 0 ? asDate(createdAtStart) : undefined,
							createdAtEnd.length > 0 ? asDate(createdAtEnd) : undefined,
						]}
						onChange={(value) =>
							set(
								["createdAtStart", value?.[0]?.toISOString() ?? ""],
								["createdAtEnd", value?.[1]?.toISOString() ?? ""],
							)
						}
					/>
				</div>

				<Table<Enrollment>
					dataSource={enrollments}
					columns={COLUMNS}
					rowKey="id"
					loading={isFetching}
					expandable={EXPANDABLE}
					pagination={{
						total,
						current: Number(page),
					}}
					onChange={handleTableChange}
					onRow={({ fullName }) => ({
						"aria-label": `Patient ${fullName}`,
					})}
				/>
			</div>
		</div>
	)
}

function Status({
	value = [],
	...props
}: Partial<SegmentedProps<Array<Enrollment["status"]>>>) {
	const options = useMemo(() => {
		return [
			{ label: "All", value: ALL_STATUSES },
			...STATUSES.map((s) => ({ label: s, value: [s] })),
		]
	}, [])
	const selectedOption = useMemo(
		() =>
			options.find((statuses) =>
				statuses.value.every((s) => value.includes(s)),
			),
		[options, value],
	)

	return (
		<div role="group" aria-label="Status" className="w-fit">
			<Segmented<Array<Enrollment["status"]>>
				{...props}
				className="w-fit"
				options={options}
				value={selectedOption?.value}
			/>
		</div>
	)
}

function Edit({ enrollment }: { enrollment: Enrollment }) {
	const { notify } = useNotification()
	const disclose = useDisclosure()
	const update = useAdminEnrollmentUpdate(enrollment.id)

	return (
		<>
			<Tooltip.Dark title={`Edit patient enrollment ${enrollment.fullName}`}>
				<Button
					type="text"
					size="large"
					icon={<EditOutlined aria-hidden />}
					aria-label={`Edit patient enrollment ${enrollment.fullName}`}
					onClick={disclose.open}
				/>
			</Tooltip.Dark>

			{disclose.isOpen && (
				<EnrollForm
					{...update}
					open
					backButtonLabel="Enrollments"
					submitButtonLabel="Save"
					title="Edit enrollment"
					enrollment={enrollment}
					onClose={disclose.close}
					onSuccess={() => {
						notify.success({
							message: "Update patient enrollment",
							description: `Patient ${enrollment.fullName} enrollment was updated successfully`,
						})
						disclose.close()
					}}
				/>
			)}
		</>
	)
}

function Toggle({
	enrollment,
	action,
}: {
	enrollment: Enrollment
	action: "cancel" | "uncancel"
}) {
	const disclose = useDisclosure()
	const toggle = useAdminEnrollmentToggle(action)
	const buttonAriaLabel =
		action === "cancel"
			? `Delete patient enrollment ${enrollment.fullName}`
			: `Restore patient enrollment ${enrollment.fullName}`
	const Icon = action === "cancel" ? DeleteOutlined : RollbackOutlined

	return (
		<>
			<Tooltip.Dark title={buttonAriaLabel}>
				<Button
					type="text"
					size="large"
					danger={action === "cancel"}
					icon={<Icon aria-hidden />}
					aria-label={buttonAriaLabel}
					onClick={disclose.open}
				/>
			</Tooltip.Dark>

			{disclose.isOpen && (
				<ToggleEnrollment
					{...toggle}
					open
					action={action}
					enrollment={enrollment}
					onCancel={disclose.close}
					onSuccess={disclose.close}
				/>
			)}
		</>
	)
}

function Ready({ enrollment }: { enrollment: Enrollment }) {
	const { notify } = useNotification()
	const disclose = useDisclosure()
	const [fieldSupportTeamId, setFieldSupportTeamId] = useState()
	const { mutate, isPending, isError } = useAdminEnrollmentReady(enrollment.id)
	const handleOk = useCallback(() => {
		if (!fieldSupportTeamId) {
			return
		}
		mutate(
			{ fieldSupportTeamId },
			{
				onSuccess: () => {
					notify.success({
						message: "Enrollment is ready for fulfillment",
					})
					disclose.close()
					setFieldSupportTeamId(undefined)
				},
			},
		)
	}, [disclose, fieldSupportTeamId, mutate, notify])

	return (
		<>
			<Tooltip.Dark title="Ready for fulfillment">
				<Button
					type="text"
					size="large"
					icon={<CheckSquareOutlined aria-hidden />}
					aria-label="Ready for fulfillment"
					onClick={disclose.open}
				/>
			</Tooltip.Dark>

			{disclose.isOpen && (
				<Modal
					open
					okButtonProps={{ disabled: !fieldSupportTeamId }}
					confirmLoading={isPending}
					title="Mark enrollment ready for fulfillment"
					okText="Confirm"
					cancelText="Cancel"
					onOk={handleOk}
					onCancel={disclose.close}
					classNames={{ body: "flex flex-col gap-2" }}
				>
					<div className="text-nowrap">Assign a field support team</div>
					<SelectTeams
						value={fieldSupportTeamId}
						onChange={setFieldSupportTeamId}
					/>
					<ErrorStatus show={isError}>
						An unexpected error occurred. Please, try again.
					</ErrorStatus>
				</Modal>
			)}
		</>
	)
}

function Complete({ enrollment }: { enrollment: Enrollment }) {
	const { notify } = useNotification()
	const disclose = useDisclosure()
	const [completionDate, setCompletionDate] = useState<Dayjs | undefined>(
		today(),
	)
	const { mutate, isPending, isError } = useAdminEnrollmentUpdate(enrollment.id)
	const minDate = enrollment.installationDate
		? asDate(enrollment.installationDate).add(1, "h")
		: undefined
	const handleOk = useCallback(() => {
		if (!completionDate) {
			return
		}
		mutate(
			{
				completionDate: completionDate.toISOString(),
				isInstallationComplete: true,
			},
			{
				onSuccess: () => {
					notify.success({
						message: "Installation is complete",
					})
					disclose.close()
					setCompletionDate(undefined)
				},
			},
		)
	}, [completionDate, disclose, mutate, notify])

	return (
		<>
			<Tooltip.Dark title="Complete installation">
				<Button
					type="text"
					size="large"
					icon={<FileDoneOutlined aria-hidden />}
					aria-label="Complete installation"
					onClick={disclose.open}
				/>
			</Tooltip.Dark>

			{disclose.isOpen && (
				<Modal
					open
					okButtonProps={{ disabled: !completionDate }}
					confirmLoading={isPending}
					title="Mark installation complete"
					okText="Confirm"
					cancelText="Cancel"
					onOk={handleOk}
					onCancel={disclose.close}
					classNames={{ body: "flex flex-col gap-2" }}
				>
					<div className="text-nowrap">Completion date</div>
					<DatePicker
						use12Hours
						aria-label="Completion date"
						format="MMM D, YYYY h:mm a"
						needConfirm={false}
						maxDate={today().endOf("day")}
						minDate={minDate}
						showTime
						value={completionDate}
						onChange={(date) => setCompletionDate(date ?? undefined)}
					/>
					<ErrorStatus show={isError}>
						An unexpected error occurred. Please, try again.
					</ErrorStatus>
				</Modal>
			)}
		</>
	)
}

const EXPANDABLE: ExpandableConfig<Enrollment> = {
	expandedRowRender: (enrollment) => {
		const details = [
			["ID", enrollment.id],
			["Name", enrollment.fullName],
			["Status", enrollment.status],
			["Organization", enrollment.careOrganization?.name],
			["Address", enrollment.address],
			["Date of birth", formatDate(enrollment.dateOfBirth)],
			["Biological sex", enrollment.biologicalSex],
			["Phone number", prettyprintPhoneNumber(enrollment.phoneNumber)],
			["Email", enrollment.email ?? DEFAULT_PLACEHOLDER],
			["Medical record number", enrollment.medicalRecordNumber],
			[
				"Last recorded weight",
				enrollment.lastRecordedWeightPounds
					? `${enrollment.lastRecordedWeightPounds} lbs`
					: DEFAULT_PLACEHOLDER,
			],
			[
				"Insurance",
				enrollment.insurances
					?.map(({ name, custom }) => custom ?? name)
					.join(", "),
			],
			["Signed consent form", <ConsentActions enrollment={enrollment} />],
			[
				"Medical necessity form",
				<MedicalNecessityFormActions enrollment={enrollment} />,
			],
			[
				"Notes",
				<span className="whitespace-pre-line">{enrollment.notes}</span>,
			],
			[
				"Alternate contacts",
				<AlternateContactsList
					alternateContacts={enrollment.alternateContacts}
					permission="view"
				/>,
			],
			["Created at", renderDate(enrollment.createdAt, enrollment, 0)],
			["Updated at", renderDate(enrollment.updatedAt, enrollment, 0)],
			[
				"Installation date",
				renderDate(enrollment.installationDate, enrollment, 0),
			],
			["Completion date", renderDate(enrollment.completionDate, enrollment, 0)],
			["Cancel date", renderDate(enrollment.cancelDate, enrollment, 0)],
		]

		return (
			<dl
				className="columns-xs space-y-2 text-navy-100"
				aria-label={`Enrollment details for ${enrollment.fullName}`}
			>
				{details.map((detail) => {
					const [title, description] = detail as [ReactNode, ReactNode]
					return (
						<dt key={title as string} className="flex items-start gap-2">
							<span className="text-nowrap text-navy-60">{title}:</span>
							{description}
						</dt>
					)
				})}
			</dl>
		)
	},
}

const getStatusColor = memoize(
	(status: Enrollment["status"]): TagProps["color"] => {
		if (status === "Pending") {
			return "gold"
		} else if (status === "Form completed") {
			return "purple"
		} else if (status === "Ready for fulfillment") {
			return "green"
		} else if (status === "Installation date set") {
			return "default"
		} else if (status === "Done") {
			return "blue"
		} else if (status === "Canceled") {
			return "red"
		}
		return undefined
	},
)

const renderDate: NonNullable<ColumnType<Enrollment>["render"]> = memoize(
	(value) => (value ? asDate(value).format("MMM D, YYYY h:mm a z") : null),
)

const COLUMNS: TableEnrollmentsType["columns"] = [
	{
		key: "operation",
		title: "Actions",
		fixed: "left",
		render(_, record) {
			const isEditable = [
				"Pending",
				"Form completed",
				"Ready for fulfillment",
			].includes(record.status)
			const isReadyForFulfillment = record.status === "Form completed"
			const isCancelable = ["Pending", "Form completed"].includes(record.status)
			const isRestorable = record.status === "Canceled"
			const isConcludable = record.status === "Installation date set"

			return (
				<>
					{isEditable && <Edit enrollment={record} />}
					{isReadyForFulfillment && <Ready enrollment={record} />}
					{isConcludable && <Complete enrollment={record} />}
					{isCancelable && <Toggle enrollment={record} action="cancel" />}
					{isRestorable && <Toggle enrollment={record} action="uncancel" />}
				</>
			)
		},
	},
	{
		dataIndex: "fullName",
		key: "fullName",
		title: "Name",
		sorter: true,
	},
	{
		dataIndex: "careOrganization",
		key: "careOrganization",
		title: "Organization",
		sorter: true,
		render(value) {
			return value ? value.name : null
		},
	},
	{
		dataIndex: "status",
		key: "status",
		title: "Status",
		sorter: true,
		render(value) {
			return value ? <Tag color={getStatusColor(value)}>{value}</Tag> : null
		},
	},
	{
		dataIndex: "consentFormUrl",
		key: "consentFormUrl",
		title: "Consent form link",
		render(value) {
			return value ? (
				<Button
					type="link"
					href={value}
					target="_blank"
					rel="noopener noreferrer"
					className="h-fit p-0"
				>
					View
				</Button>
			) : null
		},
	},
	{
		dataIndex: "medicalNecessityForm",
		key: "medicalNecessityForm",
		title: "With medical necessity form?",
		render(value, record) {
			if (value) {
				return <MedicalNecessityFormActions enrollment={record} />
			}
			return <Tag color="warning">NO</Tag>
		},
	},
	{
		dataIndex: "address",
		key: "address",
		title: "Address",
		sorter: true,
		render(value) {
			return value ? (
				<div className="flex min-w-0">
					<AddressLink>{value}</AddressLink>
				</div>
			) : null
		},
	},
	{
		dataIndex: "dateOfBirth",
		key: "dateOfBirth",
		title: "Date of birth",
		render(value) {
			return value ? asDate(value).format("MMM D, YYYY") : null
		},
	},
	{
		dataIndex: "biologicalSex",
		key: "biologicalSex",
		title: "Biological sex",
		sorter: true,
		render(value) {
			return value ? (
				<Tag color={value === "Female" ? "purple" : "orange"}>{value}</Tag>
			) : null
		},
	},
	{
		dataIndex: "email",
		key: "email",
		title: "Email",
		sorter: true,
	},
	{
		dataIndex: "phoneNumber",
		key: "phoneNumber",
		title: "Phone number",
		render(value) {
			return value ? prettyprintPhoneNumber(value) : null
		},
	},
	{
		dataIndex: "medicalRecordNumber",
		key: "medicalRecordNumber",
		title: "Medical record number",
		sorter: true,
	},
	{
		dataIndex: "lastRecordedWeightPounds",
		key: "lastRecordedWeightPounds",
		title: "Last recorded weight",
		sorter: true,
		render(value) {
			return value ? `${value} lbs` : null
		},
	},
	{
		dataIndex: "insurances",
		key: "insurances",
		title: "Insurances",
		render(_, { insurances }) {
			if (!insurances) {
				return null
			}
			return insurances.map(({ name, custom }) => (
				<Tag key={custom ?? name}>{custom ?? name}</Tag>
			))
		},
	},
	{
		dataIndex: "alternateContacts",
		key: "alternateContacts",
		title: "Alternate contacts",
		render(value) {
			return value ? <AlternateContactsList alternateContacts={value} /> : null
		},
	},
	{
		dataIndex: "createdAt",
		key: "createdAt",
		title: "Created at",
		sorter: true,
		render: renderDate,
	},
	{
		dataIndex: "updatedAt",
		key: "updatedAt",
		title: "Updated at",
		sorter: true,
		render: renderDate,
	},
	{
		dataIndex: "installationDate",
		key: "installationDate",
		title: "Installation date",
		sorter: true,
		render: renderDate,
		onCell({ status }) {
			return {
				className: status === "Installation date set" ? "bg-mint-60" : "",
			}
		},
	},
	{
		dataIndex: "completionDate",
		key: "completionDate",
		title: "Completion date",
		sorter: true,
		render: renderDate,
		onCell({ status }) {
			return { className: status === "Done" ? "bg-mint-60" : "" }
		},
	},
	{
		dataIndex: "cancelDate",
		key: "cancelDate",
		title: "Cancel date",
		sorter: true,
		render: renderDate,
	},
]

type TableEnrollmentsType = ComponentProps<typeof Table<Enrollment>>

const ALL_STATUSES = STATUSES.map((s) => s)
